import ReactQuill, { Quill } from "react-quill"
import "react-quill/dist/quill.snow.css"
import "./RichTextEditor.css"
import { useLanguage } from "../context/LanguageProvider"

// 1. Extend the Quill Link format to preserve target and rel attributes
const Link = Quill.import("formats/link")

class CustomLink extends Link {
  static create(value) {
    const node = super.create(value)
    node.setAttribute("target", "_blank")
    node.setAttribute("rel", "noopener noreferrer")
    return node
  }

  format(name, value) {
    if (name === "link" && value) {
      super.format(name, value)
      this.domNode.setAttribute("target", "_blank")
      this.domNode.setAttribute("rel", "noopener noreferrer")
    } else {
      super.format(name, value)
    }
  }
}

Quill.register(CustomLink, true)

// 2. Use inline styles for direction/align so the saved HTML keeps them
//    (class-based output like "ql-direction-rtl" only works inside the editor)
const DirectionStyle = Quill.import("attributors/style/direction")
const AlignStyle = Quill.import("attributors/style/align")
Quill.register(DirectionStyle, true)
Quill.register(AlignStyle, true)

// 3. Toolbar icons for the LTR / RTL buttons
const icons = Quill.import("ui/icons")

icons["direction-ltr"] = `
<svg viewBox="0 0 18 18">
  <polygon class="ql-stroke ql-fill" points="3 11 5 9 3 7 3 11"></polygon>
  <line class="ql-stroke ql-fill" x1="15" x2="11" y1="4" y2="4"></line>
  <path class="ql-fill" d="M11,3a3,3,0,0,0,0,6h1V3H11Z"></path>
  <rect class="ql-fill" height="11" width="1" x="11" y="4"></rect>
  <rect class="ql-fill" height="11" width="1" x="13" y="4"></rect>
</svg>`

icons["direction-rtl"] = `
<svg viewBox="0 0 18 18">
  <polygon class="ql-stroke ql-fill" points="15 12 13 10 15 8 15 12"></polygon>
  <line class="ql-stroke ql-fill" x1="15" x2="11" y1="4" y2="4"></line>
  <path class="ql-fill" d="M11,3a3,3,0,0,0,0,6h1V3H11Z"></path>
  <rect class="ql-fill" height="11" width="1" x="11" y="4"></rect>
  <rect class="ql-fill" height="11" width="1" x="13" y="4"></rect>
</svg>`

function setDirection(quill, dir) {
  const range = quill.getSelection(true)
  if (!range) return

  // LTR is the default, so remove the attribute instead of storing "ltr"
  quill.formatLine(range.index, range.length, "direction", dir === "rtl" ? "rtl" : false)
  quill.formatLine(range.index, range.length, "align", dir === "rtl" ? "right" : false)
}

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "1" }],
      [{ align: [] }],
      ["direction-ltr", "direction-rtl"],
      ["blockquote", "link"],
      ["clean"],
    ],
    handlers: {
      "direction-ltr": function () {
        setDirection(this.quill, "ltr")
      },
      "direction-rtl": function () {
        setDirection(this.quill, "rtl")
      },
      link: function () {
        const url = prompt("Enter URL:")

        if (!url) return

        const normalizedUrl = /^https?:\/\//i.test(url)
          ? url
          : `https://${url}`

        const range = this.quill.getSelection()

        if (range) {
          if (range.length === 0) {
            // Insert link with the text as the URL if no selection
            this.quill.insertText(range.index, normalizedUrl, "link", normalizedUrl)
          } else {
            this.quill.formatText(
              range.index,
              range.length,
              "link",
              normalizedUrl
            )
          }
        }
      },
    },
  },
}

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "indent",
  "align",
  "direction",
  "blockquote",
  "link",
]

const EMPTY_HTML = "<p><br></p>"

export default function RichTextEditor({
  id,
  value,
  onChange,
  placeholder,
}) {
  const handleChange = (html) => {
    if (html === EMPTY_HTML) {
      onChange("")
      return
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    doc.querySelectorAll("a").forEach((link) => {
      let href = link.getAttribute("href")

      if (href && !/^https?:\/\//i.test(href)) {
        href = `https://${href}`
      }

      link.setAttribute("href", href)
      link.setAttribute("target", "_blank")
      link.setAttribute("rel", "noopener noreferrer")
    })

    onChange(doc.body.innerHTML)
  }

  return (
    <div className="rich-editor w-full text-xs outline-none border-2 border-gray-200 rounded-2xl transition-all duration-200 bg-gray-50 focus-within:bg-white focus-within:border-[var(--color-primary-500)] focus-within:ring-4 focus-within:ring-[var(--color-primary-100)]">
      <ReactQuill
        theme="snow"
        id={id}
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
    </div>
  )
}